import React from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  LayoutDashboard,
  Wrench,
  Users,
  Package,
  ShoppingCart,
  Receipt,
  BarChart3,
  Settings,
  X,
  FileText,
  Layers,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';


const Sidebar = ({ isOpen, onClose, isCollapsed, onToggleCollapse }) => {
  const location = useLocation();
  const { user, hasPermission } = useAuth();

  const navigation = [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: LayoutDashboard,
    },

    {
      name: 'Jobs',
      href: '/jobs',
      icon: Wrench,
    },
    {
      name: 'Billing',
      href: '/billing',
      icon: Receipt,
    },
    {
      name: 'Customers',
      href: '/customers',
      icon: Users,
    },
    {
      name: 'Products',
      href: '/products',
      icon: Package,
    },
    {
      name: 'Inventory',
      href: '/inventory',
      icon: ShoppingCart,
    },
    {
      name: 'Expenses',
      href: '/expenses',
      icon: Receipt,
    },
    {
      name: 'Statement',
      href: '/statement',
      icon: FileText,
    },
    {
      name: 'Reports',
      href: '/reports',
      icon: BarChart3,
    },
    {
      name: 'SaaS Portal',
      href: '/saas',
      icon: Layers,
    },
    {
      name: 'Settings',
      href: '/settings',
      icon: Settings,
    },
  ];

  const filteredNavigation = navigation.filter(item => {
    // 1. Hide SaaS Portal for Client admins entirely
    if (item.name === 'SaaS Portal') return false;

    // Hide Settings for Employees
    if (item.name === 'Settings' && user?.role === 'EMPLOYEE') return false;

    // 2. Role-based view permission check
    if (hasPermission && !hasPermission(item.name, 'view')) {
      return false;
    }

    // 3. Enforce active subscription plan boundaries (dynamic or static fallback)
    if (user && (user.role === 'CLIENT' || user.role === 'ADMIN' || user.role === 'EMPLOYEE')) {
      const planName = user.plan || 'Starter';
      
      // Attempt to load dynamically created plans from Super Admin
      try {
        const storedPlans = localStorage.getItem('saas_plans');
        if (storedPlans) {
          const customPlans = JSON.parse(storedPlans);
          if (customPlans[planName] && Array.isArray(customPlans[planName].allowedFeatures)) {
            return customPlans[planName].allowedFeatures.includes(item.name);
          }
        }
      } catch (err) {
        console.error("Error parsing dynamic saas_plans in Sidebar:", err);
      }

      // Hardcoded fallback logic
      if (planName === 'Starter') {
        return ['Dashboard', 'Jobs', 'Billing', 'Customers', 'Settings'].includes(item.name);
      }
      if (planName === 'Growth') {
        return ['Dashboard', 'Jobs', 'Billing', 'Customers', 'Products', 'Inventory', 'Expenses', 'Statement', 'Settings'].includes(item.name);
      }
      // Enterprise package unlocks everything
      return true;
    }
    return true;
  });

  return (
    <>
      {/* Desktop Sidebar */}
      <div className="hidden lg:flex lg:flex-shrink-0">
        <div className={`flex flex-col transition-all duration-300 ${isCollapsed ? 'w-20' : 'w-64'}`}>
          <div className="flex flex-col flex-grow bg-white border-r border-gray-200 pt-5 pb-4 overflow-y-auto">
            <div className={`flex items-center flex-shrink-0 px-4 ${isCollapsed ? 'justify-center' : ''}`}>
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center shrink-0">
                <span className="text-white font-bold text-sm">RS</span>
              </div>
              {!isCollapsed && (
                <span className="ml-3 text-lg font-semibold text-gray-900 truncate">
                  Repair Shop
                </span>
              )}
            </div>
            <nav className="mt-8 flex-1 px-2 space-y-1">
              {filteredNavigation.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.href || 
                  (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
                
                return (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={`sidebar-link ${isActive ? 'active' : ''} ${isCollapsed ? 'justify-center px-1 py-3' : ''}`}
                    title={isCollapsed ? item.name : ''}
                  >
                    <Icon className={`w-5 h-5 ${isCollapsed ? 'mr-0' : 'mr-3'}`} />
                    {!isCollapsed && <span>{item.name}</span>}
                  </NavLink>
                );
              })}
            </nav>

            {/* Collapse Toggle Button */}
            <div className="border-t border-gray-200 p-2">
              <button
                onClick={onToggleCollapse}
                className="flex items-center justify-center w-full p-2 text-gray-500 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
                title={isCollapsed ? "Expand Sidebar" : "Collapse Sidebar"}
              >
                {isCollapsed ? (
                  <ChevronRight className="w-5 h-5" />
                ) : (
                  <div className="flex items-center space-x-2 w-full px-2">
                    <ChevronLeft className="w-4 h-4 shrink-0" />
                    <span className="text-xs font-semibold truncate">Collapse Sidebar</span>
                  </div>
                )}
              </button>
            </div>

            {/* Branding Footer */}
            {!isCollapsed && (
              <div className="border-t border-gray-200 px-4 py-3">
                <p className="text-xs text-gray-400 text-center leading-tight">
                  Design & Developed by<br />
                  <span className="font-medium text-gray-500">Smartgonext Software Solution</span>
                </p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Mobile Sidebar */}
      <div className={`lg:hidden fixed inset-y-0 left-0 z-50 w-64 bg-white transform ${
        isOpen ? 'translate-x-0' : '-translate-x-full'
      } transition-transform duration-300 ease-in-out`}>
        <div className="flex flex-col h-full">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">RS</span>
              </div>
              <span className="ml-3 text-lg font-semibold text-gray-900">
                Repair Shop
              </span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto">
            {filteredNavigation.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href || 
                (item.href !== '/dashboard' && location.pathname.startsWith(item.href));
              
              return (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={onClose}
                  className={`sidebar-link ${isActive ? 'active' : ''}`}
                >
                  <Icon className="w-5 h-5 mr-3" />
                  {item.name}
                </NavLink>
              );
            })}
          </nav>

          {/* Branding Footer for Mobile */}
          <div className="border-t border-gray-200 px-4 py-3">
            <p className="text-xs text-gray-400 text-center leading-tight">
              Design & Developed by<br />
              <span className="font-medium text-gray-500">Smartgonext Software Solution</span>
            </p>
          </div>
        </div>
      </div>
    </>
  );

};

export default Sidebar;